import React from "react";
import { FormFeedback, Input, Label } from "reactstrap";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import Flatpickr from "react-flatpickr";
import { filterSelectStyles } from "Components/Common/TableFilters";
import CountrySelect, { isKnownCountry } from "Components/Common/CountrySelect";

/**
 * Form controls for the record manager. Each binds to one Formik form by
 * field name, shows its error once the field has been touched or a save was
 * attempted, and goes read-only with the rest of the form.
 */

export type Option = { value: any; label: string };

type FieldProps = {
  form: any;
  name: string;
  label: string;
  required?: boolean;
  readOnly?: boolean;
  hint?: string;
};

const errorOf = (form: any, name: string) =>
  (form.touched[name] || form.submitCount > 0) && form.errors[name]
    ? String(form.errors[name])
    : "";

const FieldLabel = ({
  htmlFor,
  label,
  required,
}: {
  htmlFor: string;
  label: string;
  required?: boolean;
}) => (
  <Label htmlFor={htmlFor} className="form-label">
    {label}
    {required ? (
      <span className="text-ink-danger ms-1" aria-hidden="true">
        *
      </span>
    ) : null}
  </Label>
);

const Hint = ({ id, hint }: { id: string; hint?: string }) =>
  hint ? (
    <div id={id} className="form-text text-ink-muted">
      {hint}
    </div>
  ) : null;

export const TextField = ({
  form,
  name,
  label,
  required,
  readOnly,
  hint,
  placeholder,
  multiline,
}: FieldProps & { placeholder?: string; multiline?: boolean }) => {
  const id = `record-${name}`;
  const error = errorOf(form, name);
  return (
    <div className="mb-3">
      <FieldLabel htmlFor={id} label={label} required={required} />
      <Input
        id={id}
        name={name}
        type={multiline ? "textarea" : "text"}
        rows={multiline ? 4 : undefined}
        placeholder={placeholder}
        value={form.values[name] ?? ""}
        onChange={form.handleChange}
        onBlur={form.handleBlur}
        invalid={!!error}
        disabled={readOnly}
        aria-describedby={hint ? `${id}-hint` : undefined}
      />
      {error ? <FormFeedback>{error}</FormFeedback> : null}
      <Hint id={`${id}-hint`} hint={hint} />
    </div>
  );
};

export const DateField = ({
  form,
  name,
  label,
  required,
  readOnly,
  hint,
  placeholder = "Select a date",
}: FieldProps & { placeholder?: string }) => {
  const id = `record-${name}`;
  const error = errorOf(form, name);
  const value = form.values[name];
  return (
    <div className="mb-3">
      <FieldLabel htmlFor={id} label={label} required={required} />
      <div className="position-relative">
        {/* @ts-ignore */}
        <Flatpickr
          id={id}
          name={name}
          className={`form-control record-manager__date${error ? " is-invalid" : ""}`}
          placeholder={placeholder}
          disabled={readOnly}
          options={{ dateFormat: "d M Y", allowInput: false }}
          value={value || ""}
          onChange={(dates: Date[]) => {
            form.setFieldValue(name, dates[0] || null);
            form.setFieldTouched(name, true, false);
          }}
          aria-describedby={hint ? `${id}-hint` : undefined}
        />
        {value && !readOnly ? (
          <button
            type="button"
            className="btn btn-link text-muted p-0 position-absolute top-50 end-0 translate-middle-y me-3 lh-1"
            aria-label={`Clear ${label.toLowerCase()}`}
            title={`Clear ${label.toLowerCase()}`}
            onClick={() => form.setFieldValue(name, null)}
          >
            <i className="ri-close-circle-fill fs-16"></i>
          </button>
        ) : null}
      </div>
      {error ? <div className="invalid-feedback d-block">{error}</div> : null}
      <Hint id={`${id}-hint`} hint={hint} />
    </div>
  );
};

export const SwitchField = ({ form, name, label, readOnly, hint }: FieldProps) => {
  const id = `record-${name}`;
  return (
    <div className="mb-3">
      <div className="form-check form-switch">
        <Input
          id={id}
          name={name}
          type="switch"
          role="switch"
          className="form-check-input"
          checked={!!form.values[name]}
          onChange={(e) => form.setFieldValue(name, e.target.checked)}
          disabled={readOnly}
          aria-describedby={hint ? `${id}-hint` : undefined}
        />
        <Label htmlFor={id} className="form-check-label">
          {label}
        </Label>
      </div>
      <Hint id={`${id}-hint`} hint={hint} />
    </div>
  );
};

/** Select styles from the filter bar, plus an error border and a menu that
 * renders above the modal instead of inside its scrolling panes. */
const recordSelectStyles = (invalid: boolean) => ({
  ...filterSelectStyles,
  control: (provided: any, state: any) => ({
    ...filterSelectStyles.control(provided, state),
    borderColor: invalid
      ? "var(--vz-form-invalid-border-color)"
      : state.isFocused
      ? "var(--vz-primary-border-subtle)"
      : "var(--vz-input-border-custom)",
    opacity: state.isDisabled ? 0.65 : 1,
    backgroundColor: state.isDisabled
      ? "var(--vz-secondary-bg-subtle, var(--vz-light))"
      : "var(--vz-input-bg-custom)",
  }),
  menuPortal: (provided: any) => ({ ...provided, zIndex: 1065 }),
});

type SelectFieldProps = FieldProps & {
  placeholder: string;
  noOptionsMessage?: string;
};

export const AsyncSelectField = ({
  form,
  name,
  label,
  required,
  readOnly,
  hint,
  placeholder,
  noOptionsMessage = "No matches. Check the spelling, or add the record first.",
  loadOptions,
}: SelectFieldProps & { loadOptions: (input: string) => Promise<Option[]> }) => {
  const id = `record-${name}`;
  const error = errorOf(form, name);
  return (
    <div className="mb-3">
      <FieldLabel htmlFor={id} label={label} required={required} />
      <AsyncSelect
        inputId={id}
        name={name}
        cacheOptions
        defaultOptions
        isClearable={!required}
        isDisabled={readOnly}
        placeholder={placeholder}
        value={form.values[name]?.value ? form.values[name] : null}
        loadOptions={loadOptions}
        onChange={(option: any) => form.setFieldValue(name, option || null)}
        onBlur={() => form.setFieldTouched(name, true)}
        noOptionsMessage={() => noOptionsMessage}
        menuPortalTarget={document.body}
        styles={recordSelectStyles(!!error)}
        aria-invalid={!!error}
        aria-describedby={hint ? `${id}-hint` : undefined}
      />
      {error ? <div className="invalid-feedback d-block">{error}</div> : null}
      <Hint id={`${id}-hint`} hint={hint} />
    </div>
  );
};

export const OptionsSelectField = ({
  form,
  name,
  label,
  required,
  readOnly,
  hint,
  placeholder,
  options,
  isLoading,
  formatOptionLabel,
}: SelectFieldProps & {
  options: Option[];
  isLoading?: boolean;
  formatOptionLabel?: (option: Option) => React.ReactNode;
}) => {
  const id = `record-${name}`;
  const error = errorOf(form, name);
  return (
    <div className="mb-3">
      <FieldLabel htmlFor={id} label={label} required={required} />
      <Select
        inputId={id}
        name={name}
        isClearable={!required}
        isDisabled={readOnly}
        isLoading={isLoading}
        placeholder={placeholder}
        options={options}
        value={form.values[name]?.value ? form.values[name] : null}
        onChange={(option: any) => form.setFieldValue(name, option || null)}
        onBlur={() => form.setFieldTouched(name, true)}
        formatOptionLabel={formatOptionLabel}
        menuPortalTarget={document.body}
        styles={recordSelectStyles(!!error)}
        aria-invalid={!!error}
        aria-describedby={hint ? `${id}-hint` : undefined}
      />
      {error ? <div className="invalid-feedback d-block">{error}</div> : null}
      <Hint id={`${id}-hint`} hint={hint} />
    </div>
  );
};

export const CountryField = ({ form, name, label, required, readOnly, hint }: FieldProps) => {
  const id = `record-${name}`;
  const error = errorOf(form, name);
  const value = form.values[name];
  const legacy = value && !isKnownCountry(value);
  const note = legacy
    ? "Saved as free text. Choose a country from the list to tidy it."
    : hint;
  return (
    <div className="mb-3">
      <FieldLabel htmlFor={id} label={label} required={required} />
      <CountrySelect
        inputId={id}
        value={value}
        onChange={(next) => form.setFieldValue(name, next)}
        onBlur={() => form.setFieldTouched(name, true)}
        invalid={!!error}
        isDisabled={readOnly}
        describedBy={note ? `${id}-hint` : undefined}
      />
      {error ? <div className="invalid-feedback d-block">{error}</div> : null}
      <Hint id={`${id}-hint`} hint={note} />
    </div>
  );
};

/**
 * A period entered as a number and a unit ("5" + "years"). Older free-text
 * values that do not read as a period are kept, and named, until replaced.
 */
export const PeriodField = ({
  form,
  countName,
  unitName,
  rawName,
  label,
  readOnly,
  hint,
}: {
  form: any;
  countName: string;
  unitName: string;
  rawName: string;
  label: string;
  readOnly?: boolean;
  hint?: string;
}) => {
  const id = `record-${countName}`;
  const error = errorOf(form, countName);
  const raw = form.values[rawName];
  const note =
    raw && !form.values[countName]
      ? `Saved as “${raw}”. Enter a number and unit to replace it.`
      : hint;
  return (
    <div className="mb-3">
      <FieldLabel htmlFor={id} label={label} />
      <div className="input-group">
        <Input
          id={id}
          name={countName}
          type="number"
          inputMode="numeric"
          min={1}
          step={1}
          placeholder="For example 5"
          value={form.values[countName] ?? ""}
          onChange={form.handleChange}
          onBlur={form.handleBlur}
          invalid={!!error}
          disabled={readOnly}
          aria-describedby={note ? `${id}-hint` : undefined}
        />
        <Input
          name={unitName}
          type="select"
          className="record-manager__unit"
          aria-label={`${label} unit`}
          value={form.values[unitName]}
          onChange={form.handleChange}
          disabled={readOnly}
        >
          <option value="years">years</option>
          <option value="months">months</option>
        </Input>
      </div>
      {error ? <div className="invalid-feedback d-block">{error}</div> : null}
      <Hint id={`${id}-hint`} hint={note} />
    </div>
  );
};
